import { useContext, useEffect, useRef, useState } from 'react'
import firebase from '../firebase/compat'
import { client } from './Client'
import { synthesizeTemplateHierarchyData } from '../components/data/templateRecordSynthesis'
import clampToFirestoreMs from '../helpers/dataHelpers/clampToFirestoreMs'
import PublicReportContext from '../StateManagment/PublicReportContext'
import callPublicReportData from '../helpers/liveReportHelpers/callPublicReportData'

// AI-CONTEXT: See docs/features/livereport-widgets-dashboard-parity.md
//   When `templateData` is set, the Cloud Function call is skipped and
//   records are synthesised from the template blob via
//   src/components/data/templateRecordSynthesis.js — so widget template
//   mode picks the correct chart variant (moment vs development) even
//   though the user is logged into their own tenant.

/**
 * useHierarchyData — generic hook for fetching data via the fetchHierarchyData Cloud Function.
 *
 * Replaces direct calls to `fetchSyncedDataWithHierarchy` in visualization components.
 * The Cloud Function is the single source of truth for how response data is fetched
 * and aggregated across the sync hierarchy.
 *
 * USAGE:
 *   const { records, hierarchy, totalRespondents, loading, error } = useHierarchyData({
 *     syncType: 'Question',
 *     id: fieldId,
 *     startDate,
 *     endDate,
 *     researchIds,
 *   })
 *
 * PARAMS:
 *   syncType           — 'Question' | 'MSIEffect' | 'Event' | 'OutputEvent'
 *   id                 — the field/event/output ID to fetch data for
 *   startDate, endDate — Timestamp bounds
 *   researchIds        — optional array of research IDs to scope to
 *   personaId          — optional persona filter
 *   filterByResearchId — explicit opt-in for research filtering (default: false)
 *
 * RETURNS:
 *   { records, hierarchy, respondentsPerProject, totalRespondents, totalDocs, loading, error }
 *
 * CACHING:
 *   Results are cached in-memory by a key derived from all params.
 *   Navigating away and back won't re-fetch if params haven't changed.
 */

// In-memory cache shared across all hook instances (lives for the session)
const cache = new Map()

// In-flight requests by cache key. Several hook instances mount with the same
// params in one render pass (chart child + QuestionData popover); without this
// each would fire its own Cloud Function call before the first resolves.
const pending = new Map()

/**
 * Empties the session cache so the next mount re-fetches. Call this after a
 * write that changes response data outside the normal flow (e.g. the dataset
 * import) — otherwise dashboards stay stale until a hard browser refresh
 * (inbox #83).
 */
export const clearHierarchyDataCache = () => {
    cache.clear()
    pending.clear()
}

const buildCacheKey = (params) => JSON.stringify(params)

/**
 * The hook's result shape from a fetchHierarchyData / getPublicLiveReportData body.
 *
 * @param {object} data - Response body
 */
const toValue = (data) => ({
    records: data.records || [],
    hierarchy: data.hierarchy || {},
    respondentsPerProject: data.respondentsPerProject || {},
    totalRespondents: data.totalRespondents || 0,
    totalDocs: data.totalDocs || 0,
})

/**
 * Public report view (no login): fetchHierarchyData refuses an anonymous
 * caller, so the same params go to getPublicLiveReportData, which takes the
 * tenant from the report itself.
 * AI-CONTEXT: See docs/features/public-livereport-data.md
 *
 * @param {import('../StateManagment/PublicReportContext').PublicReport} publicReport
 * @param {object} params - The fetchHierarchyData params
 */
const fetchPublic = (publicReport, { companyId, personaId, ...params }) => callPublicReportData({
    reportId: publicReport.reportId,
    code: publicReport.code,
    kind: 'hierarchy',
    ...params,
    ...(personaId ? { personaId } : {}),
})

/**
 * Cache key for one request. Public and logged-in results never share an entry.
 *
 * @param {object} params - The fetchHierarchyData params
 * @param {import('../StateManagment/PublicReportContext').PublicReport|null} publicReport
 * @returns {string}
 */
const cacheKeyFor = (params, publicReport) =>
    buildCacheKey(publicReport ? { ...params, publicReportId: publicReport.reportId } : params)

/**
 * One fetch through the right door — the public function in a shared report,
 * fetchHierarchyData otherwise — cached, and joined with an identical request
 * that is already in flight. Shared by the hook and callFetchHierarchyData.
 *
 * @param {object} params - The fetchHierarchyData params
 * @param {import('../StateManagment/PublicReportContext').PublicReport|null} publicReport
 * @returns {Promise<object>}
 */
const requestHierarchyData = (params, publicReport) => {
    const cacheKey = cacheKeyFor(params, publicReport)
    if (cache.has(cacheKey)) return Promise.resolve(cache.get(cacheKey))

    let request = pending.get(cacheKey)
    if (!request) {
        const fetched = publicReport
            ? fetchPublic(publicReport, params)
            : firebase.app().functions('europe-west1').httpsCallable('fetchHierarchyData')(params)
                .then((result) => result.data || {})
        request = fetched
            .then((data) => {
                const value = toValue(data)
                cache.set(cacheKey, value)
                return value
            })
            .finally(() => {
                pending.delete(cacheKey)
            })
        pending.set(cacheKey, request)
    }
    return request
}

const useHierarchyData = ({
    syncType,
    id,
    startDate,
    endDate,
    researchIds,
    personaId,
    filterByResearchId = false,
    templateData = null,
}) => {
    const [state, setState] = useState({
        records: [],
        hierarchy: {},
        respondentsPerProject: {},
        totalRespondents: 0,
        totalDocs: 0,
        loading: true,
        error: null,
    })

    const mounted = useRef(true)
    const publicReport = useContext(PublicReportContext)

    useEffect(() => {
        mounted.current = true
        return () => { mounted.current = false }
    }, [])

    useEffect(() => {
        if (!syncType || !id) {
            setState(s => ({ ...s, loading: false }))
            return
        }

        // Template mode — synthesize from pre-fetched template blob.
        // Skips the Cloud Function entirely so chart leaves see real
        // records (and pick the right moment/development variant)
        // even though the user is logged into their own tenant.
        if (templateData) {
            const synth = synthesizeTemplateHierarchyData({ syncType, id, templateData, personaId })
            if (mounted.current) {
                setState({
                    records: synth?.records || [],
                    hierarchy: synth?.hierarchy || {},
                    respondentsPerProject: synth?.respondentsPerProject || {},
                    totalRespondents: synth?.totalRespondents || 0,
                    totalDocs: synth?.totalDocs || 0,
                    loading: false,
                    error: null,
                })
            }
            return
        }

        if (startDate == null || endDate == null) {
            setState(s => ({ ...s, loading: false }))
            return
        }

        const params = {
            syncType,
            id,
            companyId: client,
            startDate: clampToFirestoreMs(startDate),
            endDate: clampToFirestoreMs(endDate),
            // The backend only applies researchIds when filterByResearchId is
            // true (bfsTraversal). Normalizing to [] when the filter is off
            // keeps the cache key stable across the ''→researchId transition,
            // so the same data isn't fetched twice.
            researchIds: filterByResearchId ? (researchIds || []) : [],
            personaId: personaId || undefined,
            filterByResearchId,
        }

        // Cached: set it synchronously, so a remount doesn't flash the spinner.
        const cacheKey = cacheKeyFor(params, publicReport)
        if (cache.has(cacheKey)) {
            const cached = cache.get(cacheKey)
            if (mounted.current) {
                setState({ ...cached, loading: false, error: null })
            }
            return
        }

        setState(s => ({ ...s, loading: true, error: null }))

        requestHierarchyData(params, publicReport)
            .then((value) => {
                if (mounted.current) {
                    setState({ ...value, loading: false, error: null })
                }
            })
            .catch((err) => {
                console.error('useHierarchyData error:', err)
                if (mounted.current) {
                    setState(s => ({ ...s, loading: false, error: err }))
                }
            })
    }, [syncType, id, startDate, endDate, JSON.stringify(researchIds), personaId, filterByResearchId, templateData, publicReport])

    return state
}

/**
 * callFetchHierarchyData — non-hook version for use inside loops / useEffect.
 *
 * Same API as the Cloud Function, but callable from imperative code
 * (e.g. multi-ID components that loop over several IDs).
 *
 * Uses the same in-memory cache as the hook.
 *
 * startDate/endDate MUST be epoch MILLISECONDS (convert with toMillis()
 * first). A raw Firestore Timestamp serializes to a plain map on the wire and
 * the server-side `Timestamp >=` comparison then silently matches zero records.
 * AI-CONTEXT: See docs/pitfalls.md#api-callable-timestamp-vs-ms
 *
 * Pass `publicReport` (from PublicReportContext) in a shared report: the call
 * then goes through getPublicLiveReportData, like the hook does.
 *
 * @param {object} args
 * @param {import('../StateManagment/PublicReportContext').PublicReport|null} [args.publicReport]
 */
export const callFetchHierarchyData = async ({
    syncType,
    id,
    startDate,
    endDate,
    researchIds = [],
    personaId,
    filterByResearchId = false,
    publicReport = null,
}) => {
    const params = {
        syncType,
        id,
        companyId: client,
        startDate: clampToFirestoreMs(startDate),
        endDate: clampToFirestoreMs(endDate),
        researchIds,
        personaId: personaId || undefined,
        filterByResearchId,
    }

    return requestHierarchyData(params, publicReport)
}

export default useHierarchyData
